import { useMemo } from 'react';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetConfigByEntityTypeQuery } from 'src/services/custom-fields/custom-field.service';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import { ColumnDef } from './data-list.types';
import { RowWithCustomFields } from './custom-field-columns.types';
import { buildCustomFieldColumns } from './build-custom-field-columns';

/**
 * The custom field columns for one entity's list, ready to append to its registry.
 *
 * Gated exactly as the record editor is (`use-custom-fields.tsx`): no configs are fetched when the
 * feature is off or the tenant is a candidate, and `getByEntityType` returns [] server-side in the
 * first case anyway.
 *
 * `isReady` is false while the fetch is in flight. Feed it to the column picker's `registryReady`
 * so Apply waits for the registry to be complete — otherwise a user who opens the picker on a cold
 * cache saves a layout that doesn't know these columns exist. (`useColumnPreferences.save` carries
 * them forward regardless; this just avoids the situation.)
 */
export function useListCustomFieldColumns<TRow extends RowWithCustomFields>(
  entityType: CustomFieldEntityType
): { cfColumns: ColumnDef<TRow>[]; isReady: boolean } {
  const { t } = useTranslate();
  const { canAccess } = useFeatures();
  const tenantType = useOrgTenant();
  const authContext = useAuthContext();

  const enabled = Boolean(authContext)
    && canAccess(FeatureType.CUSTOM_FIELDS_ENABLED)
    && tenantType !== TenantType.Candidate;

  const { data: configs, isLoading } = useGetConfigByEntityTypeQuery(
    { entityType },
    { skip: !enabled }
  );

  const cfColumns = useMemo(() => buildCustomFieldColumns<TRow>(configs, t), [configs, t]);

  return { cfColumns, isReady: !enabled || !isLoading };
}
